/**
 * cart-progress.js
 * Auto-adds/removes the tier-2 gift product when the cart crosses the
 * configured gift_threshold. The bar markup itself re-renders server-side
 * via the Section Rendering API — see snippets/cart-progress.liquid.
 *
 * The listener dispatches a CustomEvent('cart:update', ...) with the same
 * shape used by snippets/header-actions.liquid upsell ATC, so the existing
 * cart-items-component.#handleCartUpdate picks it up and morphs the cart
 * drawer with the gift line (or without it) visible.
 */
(function () {
  if (window.__cartProgressInit) return;
  window.__cartProgressInit = true;

  var AUTO_GIFT_KEY = '_auto_gift';
  var AUTO_GIFT_VALUE = 'tier-2';
  /** cart attribute that carries the resolved discount code to checkout.
   *  Read server-side by snippets/cart-products.liquid, which renders a
   *  hidden <input name="discount"> into the cart form so Shopify applies
   *  the code on checkout submission. */
  var DISCOUNT_CODE_ATTR = '_gift_discount_code';
  var SELF_SOURCE = 'cart-progress-auto-gift';

  /** in-flight guard so multiple cart:update events don't fire parallel syncs */
  var syncing = false;
  /** set by onCartUpdate when a cart:update arrives while a sync is in
   *  flight; triggers exactly one follow-up sync once the current one
   *  finishes. Without this, a rapid "add A then add B" sequence could
   *  leave the cart past the gift threshold without the gift line, because
   *  the /cart.js fetch for A still sees a pre-B snapshot and B's
   *  cart:update is dropped by the in-flight guard. */
  var syncPending = false;
  /** set to true if an add attempt returned a Shopify error (e.g. sold-out);
   *  prevents hammering the API on every subsequent cart:update */
  var giftUnavailable = false;

  /**
   * Finds the gift line in a cart payload (from /cart.js or /cart/add.js).
   * @param {object} cart
   * @param {string|number} giftVariantId
   * @returns {object|null}
   */
  function findGiftLine(cart, giftVariantId) {
    if (!cart || !cart.items) return null;
    var vid = String(giftVariantId);
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (String(item.variant_id) !== vid) continue;
      var props = item.properties || {};
      if (props[AUTO_GIFT_KEY] === AUTO_GIFT_VALUE) return item;
    }
    return null;
  }

  /**
   * Calculates the cart total excluding the auto-gift line(s), so the
   * threshold check is independent of the gift's own price (which may not
   * yet be zeroed by the automatic discount until checkout).
   * @param {object} cart
   * @returns {number} cents
   */
  function totalExcludingGift(cart) {
    var total = cart.total_price || 0;
    if (!cart.items) return total;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var props = item.properties || {};
      if (props[AUTO_GIFT_KEY] === AUTO_GIFT_VALUE) {
        total -= item.final_line_price || 0;
      }
    }
    return total;
  }

  /**
   * Dispatches a cart:update event with the shape cart-items-component
   * expects (event.detail.data.sections[sectionId] → morph target).
   * @param {object} resource - parsed JSON from /cart/add.js or /cart/change.js
   * @param {object|null} sections - response.sections map from the POST
   */
  function dispatchCartUpdate(resource, sections) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: resource,
          data: {
            source: SELF_SOURCE,
            sections: sections || null,
          },
        },
      })
    );
  }

  /**
   * Finds the cart-items-component's section id. The wrapper exposes this
   * via data-section-id on the cart-progress element (rendered by
   * snippets/cart-progress.liquid with section.id).
   * Falls back to the first cart-items-component on the page if the
   * wrapper attr is missing (e.g. progress bar not yet in DOM).
   * @returns {string|null}
   */
  function getSectionId(wrapper) {
    var fromWrapper = wrapper && wrapper.dataset.sectionId;
    if (fromWrapper) return fromWrapper;
    var comp = document.querySelector('cart-items-component[data-section-id]');
    return comp ? comp.dataset.sectionId : null;
  }

  function addGift(giftVariantId, sectionId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: Number(giftVariantId),
            quantity: 1,
            properties: (function () {
              var p = {};
              p[AUTO_GIFT_KEY] = AUTO_GIFT_VALUE;
              return p;
            })(),
          },
        ],
        sections: sectionId || undefined,
        sections_url: window.location.pathname,
      }),
    }).then(function (r) {
      return r.json();
    });
  }

  function removeGift(lineKey, sectionId) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections: sectionId || undefined,
        sections_url: window.location.pathname,
      }),
    }).then(function (r) {
      return r.json();
    });
  }

  /**
   * Clamps the auto-gift line back to quantity 1. The storefront discount
   * code zeros the gift product's price, so any extra units would also be
   * free at checkout — effectively free stock. cart-products.liquid already
   * disables the +/- UI for this line; this is the server-side backstop
   * covering dev tools / direct /cart/change.js / cart-app edits.
   * @param {string|number} lineKey
   * @param {string|null} sectionId
   */
  function clampGiftQuantity(lineKey, sectionId) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: 1,
        sections: sectionId || undefined,
        sections_url: window.location.pathname,
      }),
    }).then(function (r) {
      return r.json();
    });
  }

  /**
   * Writes/clears the cart attribute that carries the discount code to
   * checkout. Pass `''` to clear.
   * @param {string} code
   * @param {string|null} sectionId
   * @returns {Promise<object>}
   */
  function setDiscountCodeAttribute(code, sectionId) {
    var attrs = {};
    attrs[DISCOUNT_CODE_ATTR] = code || '';
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: attrs,
        sections: sectionId || undefined,
        sections_url: window.location.pathname,
      }),
    }).then(function (r) {
      return r.json();
    });
  }

  /**
   * Silently sets Shopify's built-in discount cookie via `/discount/<code>`.
   * Belt-and-suspenders: the hidden `<input name="discount">` rendered in
   * the cart form is the authoritative mechanism; this cookie is a backup
   * in case the customer exits through a non-cart-form path (e.g. express
   * checkout buttons) on some themes.
   *
   * No-op if code is blank.
   * @param {string} code
   * @returns {Promise<void>|undefined}
   */
  function applyDiscountCookie(code) {
    if (!code) return;
    return fetch('/discount/' + encodeURIComponent(code), {
      method: 'GET',
      credentials: 'same-origin',
      redirect: 'manual',
    }).catch(function () {
      /* network/redirect errors are harmless — the form-input mechanism
         still carries the code to checkout */
    });
  }

  /**
   * Evaluates the current cart against the tier-2 threshold and
   * adds/removes the gift as needed. Guarded against re-entry so a chain
   * of cart:update events triggered by our own add/remove doesn't loop.
   */
  function sync() {
    if (syncing) {
      syncPending = true;
      return;
    }

    var wrapper = document.querySelector('[data-cart-progress]');
    if (!wrapper) return;

    var tier2CentsAttr = wrapper.dataset.tier2Cents;
    var giftVariantId = wrapper.dataset.giftVariantId;
    var discountCode = (wrapper.dataset.giftDiscountCode || '').trim();

    // Tier 2 is disabled — nothing to do.
    if (!tier2CentsAttr || !giftVariantId) return;

    var tier2Cents = parseInt(tier2CentsAttr, 10);
    if (!tier2Cents || isNaN(tier2Cents)) return;

    syncing = true;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var adjustedTotal = totalExcludingGift(cart);
        var thresholdMet = adjustedTotal >= tier2Cents;
        var giftLine = findGiftLine(cart, giftVariantId);
        var sectionId = getSectionId(wrapper);
        var currentAttr =
          (cart.attributes && cart.attributes[DISCOUNT_CODE_ATTR]) || '';

        if (thresholdMet && !giftLine) {
          if (giftUnavailable) return; // previous add failed (e.g. sold-out)
          return addGift(giftVariantId, sectionId).then(function (response) {
            if (response && !response.status) {
              // Write the discount code to cart attributes so the cart form
              // carries it to checkout. Fire-and-forget (don't block the UI
              // update) — if the attribute write fails, the hidden input
              // just won't render; the next cart:update will retry.
              if (discountCode && currentAttr !== discountCode) {
                setDiscountCodeAttribute(discountCode, sectionId);
                applyDiscountCookie(discountCode);
              }
              dispatchCartUpdate(response, response.sections);
            } else if (response && response.status) {
              // Shopify error — disable further retries, log for merchant
              giftUnavailable = true;
              if (window.console && console.warn) {
                console.warn(
                  '[cart-progress] Gift variant ' + giftVariantId + ' could not be added: ' +
                  (response.description || response.message || 'unknown error') +
                  '. Check that the product is in stock and the gift_product setting is correct.'
                );
              }
            }
          });
        }
        if (!thresholdMet && giftLine) {
          return removeGift(giftLine.key, sectionId).then(function (response) {
            if (response && !response.status) {
              // Clear the discount-code attribute so the hidden form input
              // disappears on the next render — no stale code travels to
              // checkout. Fire-and-forget.
              if (currentAttr) {
                setDiscountCodeAttribute('', sectionId);
              }
              dispatchCartUpdate(response, response.sections);
            }
          });
        }

        // Threshold met, gift present, but quantity crept above 1. Clamp.
        // This is the server-side backstop for the UI lock in
        // cart-products.liquid — covers dev tools, direct /cart/change.js
        // calls, or third-party cart apps.
        if (thresholdMet && giftLine && giftLine.quantity > 1) {
          return clampGiftQuantity(giftLine.key, sectionId).then(function (response) {
            if (response && !response.status) {
              // Keep the discount attribute in sync at the same time.
              if (discountCode && currentAttr !== discountCode) {
                setDiscountCodeAttribute(discountCode, sectionId);
                applyDiscountCookie(discountCode);
              }
              dispatchCartUpdate(response, response.sections);
            }
          });
        }

        // Threshold met, gift present, but the cart attribute is out of
        // sync with the configured code (e.g. merchant changed the code,
        // or the customer switched markets mid-session). Reconcile.
        if (thresholdMet && giftLine && discountCode && currentAttr !== discountCode) {
          setDiscountCodeAttribute(discountCode, sectionId);
          applyDiscountCookie(discountCode);
        }
        if (!thresholdMet && !giftLine && currentAttr) {
          setDiscountCodeAttribute('', sectionId);
        }
      })
      .catch(function (err) {
        if (window.console && console.warn) console.warn('[cart-progress] sync failed', err);
      })
      .finally(function () {
        syncing = false;
        // Drain one pending event if one arrived during this sync. Running
        // sync again re-fetches /cart.js (cheap, same-origin, JSON) and
        // reconciles any state change we missed.
        if (syncPending) {
          syncPending = false;
          sync();
        }
      });
  }

  /**
   * Handles cart:update events from other sources (line qty changes,
   * upsell add, variant add-to-cart, etc.) and re-evaluates the tier-2
   * state. Skips events originating from ourselves to avoid a loop.
   */
  function onCartUpdate(event) {
    var source = event && event.detail && event.detail.data && event.detail.data.source;
    if (source === SELF_SOURCE) return;
    sync();
  }

  document.addEventListener('cart:update', onCartUpdate);

  // ── Milestone celebration ──────────────────────────────────────────
  // Detects false→true transitions of the `.is-reached` class on tier
  // circles (tier 1 / tier 2) and fires a short animation: circle pop +
  // bar shimmer sweep + label pulse + confetti burst. Animations only
  // trigger on *transitions* — not on every re-render — by comparing
  // the DOM state before and after each cart:update.
  var reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var lastTierState = { tier1: null, tier2: null };

  function readTierState() {
    var wrapper = document.querySelector('[data-cart-progress]');
    if (!wrapper) return null;
    var c1 = wrapper.querySelector('.cart-progress__circle--1');
    var c2 = wrapper.querySelector('.cart-progress__circle--2');
    return {
      tier1: c1 ? c1.classList.contains('is-reached') : null,
      tier2: c2 ? c2.classList.contains('is-reached') : null,
    };
  }

  function spawnConfetti(circleEl) {
    var host = circleEl.parentElement; // .cart-progress__bar
    if (!host) return;
    var rect = circleEl.getBoundingClientRect();
    var hostRect = host.getBoundingClientRect();
    var cx = rect.left + rect.width / 2 - hostRect.left;
    var cy = rect.top + rect.height / 2 - hostRect.top;
    var colors = ['#f2bc41', '#ffd76a', '#fff4c2'];
    var count = 8;
    for (var i = 0; i < count; i++) {
      var p = document.createElement('span');
      p.className = 'cart-progress__confetti';
      var angle = (i / count) * 360 + (Math.random() * 24 - 12);
      var distance = 18 + Math.random() * 14;
      var rad = (angle * Math.PI) / 180;
      p.style.setProperty('--cp-x', (Math.cos(rad) * distance).toFixed(1) + 'px');
      p.style.setProperty('--cp-y', (Math.sin(rad) * distance).toFixed(1) + 'px');
      p.style.setProperty('--cp-rot', Math.floor(Math.random() * 270) + 'deg');
      p.style.setProperty('--cp-color', colors[i % colors.length]);
      p.style.left = cx + 'px';
      p.style.top = cy + 'px';
      host.appendChild(p);
      // Clean up after the animation ends (0.8s in CSS) — a small buffer
      // covers jank during heavy morphs.
      (function (el) {
        setTimeout(function () {
          if (el.parentNode) el.parentNode.removeChild(el);
        }, 900);
      })(p);
    }
  }

  function removeClassLater(el, cls, ms) {
    setTimeout(function () {
      if (el) el.classList.remove(cls);
    }, ms);
  }

  function celebrateCircle(circleEl, goalEl) {
    if (!circleEl || reducedMotion) return;
    circleEl.classList.add('is-just-reached');
    removeClassLater(circleEl, 'is-just-reached', 800);

    var line = circleEl.parentElement && circleEl.parentElement.querySelector('.cart-progress__line');
    if (line) {
      line.classList.add('is-just-reached');
      removeClassLater(line, 'is-just-reached', 1000);
    }

    if (goalEl) {
      goalEl.classList.add('is-just-reached');
      removeClassLater(goalEl, 'is-just-reached', 800);
    }

    spawnConfetti(circleEl);
  }

  function getGoalEl(tierIndex) {
    var goals = document.querySelectorAll('.cart-progress__goals .cart-progress__goal');
    return goals[tierIndex] || null;
  }

  function checkTransitions() {
    var curr = readTierState();
    if (!curr) return;
    var prev = lastTierState;

    // Fire when prev was *known to not be reached* (false OR null — null
    // covers the empty-cart → first-add-crossing-the-tier case, where the
    // wrapper didn't exist at init time so prev.tierN was null).
    if (prev.tier1 !== true && curr.tier1 === true) {
      var c1 = document.querySelector('.cart-progress__circle--1');
      celebrateCircle(c1, getGoalEl(0));
    }
    if (prev.tier2 !== true && curr.tier2 === true) {
      var c2 = document.querySelector('.cart-progress__circle--2');
      celebrateCircle(c2, getGoalEl(1));
    }

    lastTierState = curr;
  }

  function onCartUpdateForCelebration() {
    // Allow cart-items-component to morph the new HTML into the DOM before
    // we read class state. Two rAFs covers most morph strategies.
    requestAnimationFrame(function () {
      requestAnimationFrame(checkTransitions);
    });
  }

  function initCelebration() {
    var initial = readTierState();
    if (initial) lastTierState = initial;
    document.addEventListener('cart:update', onCartUpdateForCelebration);
  }

  // Run once on load to reconcile stale carts (e.g. user landed directly
  // on /cart or the session was modified outside the drawer).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      sync();
      initCelebration();
    });
  } else {
    sync();
    initCelebration();
  }
})();
